import React, { useContext, useEffect, useRef, useState } from 'react'
import { DefectContext } from './SingleDefect'
import API from '../../../../services/API'
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import BigModal from '../../../../components/Modals/BigModal'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'

const Images = () => {
  const { defect, setDefect } = useContext(DefectContext)
  const [loading, setLoading] = useState(true)
  const [imageURLs, setImageURLs] = useState([])
  const [vdiImageURLs, setVdiImageURLs] = useState([])
  const [openImage, setOpenImage] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(false)
  const imageRef = useRef()

  useEffect(() => {
    if (Array.isArray(defect.images) && defect.images.length === 0) {
      setLoading(false)
    } else if (Array.isArray(defect.images) && imageURLs.length === 0) {
      for (let x of defect.images) {
        API.post('/gets3downloadurl', {
          key: x,
          inlineView: true,
        })
          .then(
            (res) =>
              res.status === 200 && setImageURLs((prev) => [...prev, res.data]),
          )
          .catch((e) => console.error(e))
          .finally(() => setLoading(false))
      }
    }
    if (Array.isArray(defect?.vdi?.attachments) && vdiImageURLs.length === 0) {
      for (let x of defect.vdi.attachments) {
        API.post('/gets3downloadurl', {
          key: x,
          inlineView: true,
        })
          .then(
            (res) =>
              res.status === 200 &&
              setVdiImageURLs((prev) => [...prev, res.data]),
          )
          .catch((e) => console.error(e))
      }
    }
  }, [defect])

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    // Reset so picking the same file again after a failure re-fires onChange
    e.target.value = ''
    if (!file || uploading) return

    setUploading(true)
    setUploadError(false)
    try {
      const upload = await API.post('/gets3uploadurl', {
        folder: 'fleetequipment/defects',
        extension:
          file.type.split('/')[1] || file.name.split('.').pop().toLowerCase(),
      })
      if (upload.status !== 200 || !upload.data?.url) {
        throw new Error('Unable to prepare the upload.')
      }

      // Await the PUT and check its status (validateStatus never rejects)
      // so the key is only saved once the object is really in the bucket.
      const put = await API.put(upload.data.url, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        timeout: 0,
        skipGlobalErrorHandler: true,
      })
      if (put.status < 200 || put.status >= 300) {
        throw new Error(`Image upload failed (${put.status})`)
      }

      const { key } = upload.data
      const res = await API.put('/defect/' + defect._id, {
        images: [...(defect.images ?? []), key],
      })
      if (res.status !== 200) {
        throw new Error('Unable to save the image to the defect.')
      }
      setImageURLs((prev) => [...prev, URL.createObjectURL(file)])
      setDefect((prev) => ({
        ...prev,
        images: [...(prev.images ?? []), key],
      }))
    } catch (err) {
      console.error(err)
      setUploadError(true)
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {[...imageURLs, ...vdiImageURLs].map((image, index) => (
          <Box
            key={index}
            onClick={() => setOpenImage(image)}
            sx={{
              aspectRatio: '4 / 3',
              cursor: 'zoom-in',
              overflow: 'hidden',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              '& img': {
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                transition: 'transform 180ms ease',
              },
              '&:hover img': {
                transform: 'scale(1.03)',
              },
            }}
          >
            <img
              src={image}
              loading="lazy"
              alt={`Defect attachment ${index + 1}`}
            />
          </Box>
        ))}
        <Box
          sx={{
            minHeight: 168,
            gridColumn:
              imageURLs.length === 0
                ? { xs: 'span 1', sm: 'span 2', md: 'span 1' }
                : 'auto',
          }}
        >
          <Box
            display="flex"
            height="100%"
            justifyContent="center"
            alignItems="center"
            onClick={() => !uploading && imageRef.current.click()}
            sx={{
              cursor: uploading ? 'progress' : 'pointer',
              borderRadius: 2,
              border: '1.5px dashed',
              borderColor: 'rgba(35,154,164,0.45)',
              bgcolor: 'rgba(35,154,164,0.04)',
              transition: 'all 160ms ease',
              '&:hover': {
                bgcolor: 'rgba(35,154,164,0.09)',
                borderColor: 'contrast.main',
              },
            }}
          >
            <Stack alignItems="center" spacing={0.5}>
              {uploading ? (
                <CircularProgress size={24} sx={{ m: 1 }} />
              ) : (
                <IconButton color="contrast" aria-label="Add a defect photo">
                  <AddAPhotoIcon />
                </IconButton>
              )}
              <Typography variant="body2" fontWeight={600}>
                {uploading ? 'Uploading…' : 'Add photo'}
              </Typography>
              <Typography
                variant="caption"
                color={uploadError ? 'error' : 'text.disabled'}
              >
                {uploadError
                  ? 'Upload failed - please try again'
                  : 'JPG, PNG or HEIC'}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
      {loading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading attachments…</Typography>
        </Stack>
      )}
      <BigModal open={!!openImage} onClose={() => setOpenImage(null)}>
        <Box
          width="100%"
          height="80vh"
          justifyContent="center"
          alignItems="center"
          sx={{
            backgroundImage: `url(${openImage})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </BigModal>
      <input
        ref={imageRef}
        accept="image/*"
        type="file"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default Images
